import { Children, isValidElement } from "react";
import { SlideUpLine, FadeUp, Stagger, StaggerItem } from "@/pages/_shared/anim";

type Crumb = { label: string; to?: string };

interface PageHeaderProps {
  eyebrow: string;
  title: React.ReactNode;
  description?: string;
  crumbs?: Crumb[];
  meta?: { label: string; value: string }[];
  accentImage?: string;
}

// Render <h1> children: if children include explicit line elements (with <br/>) or strings,
// we wrap each segment in SlideUpLine. For simplicity, accept any node and just animate the
// whole heading using a SlideUpLine-style mask if it's plain text; otherwise wrap fragments.
function renderAnimatedTitle(node: React.ReactNode) {
  // Split top-level children by <br/> manually
  const lines: React.ReactNode[] = [];
  let buf: React.ReactNode[] = [];

  const push = () => {
    if (buf.length) {
      lines.push(<span key={`line-${lines.length}`}>{buf}</span>);
      buf = [];
    }
  };

  Children.forEach(node, (child) => {
    if (isValidElement(child) && (child.type as unknown) === "br") {
      push();
    } else {
      buf.push(child);
    }
  });
  push();

  return lines.map((l, i) => (
    <SlideUpLine key={i} as="div" delay={i * 0.08}>
      {l}
    </SlideUpLine>
  ));
}

export default function PageHeader({
  eyebrow,
  title,
  description,
  crumbs = [],
  meta = [],
  accentImage,
}: PageHeaderProps) {
  return (
    <section className="relative bg-crest-deep pt-32 md:pt-40 pb-16 md:pb-20 overflow-hidden">
      {accentImage && (
        <>
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: `url(${accentImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-crest-deep via-crest-deep/95 to-crest-deep/70" />
        </>
      )}
      <div
        className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, #EA7526 0%, transparent 70%)" }}
      />

      <div className="relative px-6 md:px-12">
        {crumbs.length > 0 && (
          <FadeUp>
            <nav className="flex items-center gap-2 text-xs md:text-sm text-white/55 mb-8 flex-wrap">
              {crumbs.map((c, i) => (
                <span key={`${c.label}-${i}`} className="flex items-center gap-2">
                  {c.to ? (
                    <a
                      href={c.to}
                      className="hover:text-crest-orange cursor-pointer whitespace-nowrap"
                    >
                      {c.label}
                    </a>
                  ) : (
                    <span className="text-white whitespace-nowrap">{c.label}</span>
                  )}
                  {i < crumbs.length - 1 && (
                    <i className="ri-arrow-right-s-line text-white/40"></i>
                  )}
                </span>
              ))}
            </nav>
          </FadeUp>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <FadeUp>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] text-crest-orange uppercase">
                <span className="w-7 h-px bg-crest-orange/60" /> {eyebrow}
              </span>
            </FadeUp>
            <h1 className="font-display text-white text-4xl md:text-6xl lg:text-[78px] leading-[1.02] tracking-tight font-medium mt-5">
              {renderAnimatedTitle(title)}
            </h1>
            {description && (
              <FadeUp delay={0.3}>
                <p className="text-white/70 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">
                  {description}
                </p>
              </FadeUp>
            )}
          </div>

          {meta.length > 0 && (
            <Stagger
              className="lg:col-span-4 grid grid-cols-2 gap-3"
              stagger={0.08}
              delay={0.25}
            >
              {meta.map((m) => (
                <StaggerItem
                  key={m.label}
                  className="border border-white/15 rounded-md px-4 py-3"
                >
                  <div className="font-display text-2xl text-crest-orange leading-none">
                    {m.value}
                  </div>
                  <div className="text-xs text-white/60 mt-2 leading-tight">
                    {m.label}
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </div>
    </section>
  );
}